import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * vertical-ticker — a VERBATIM port of Shopify/Shrine's `vertical-ticker` section. It pairs an editorial
 * CONTENT column (a heading with the shared word-HIGHLIGHT engine, prose richtext and a CTA button, over an
 * optional background image + overlay) with a coloured TICKER column: a vertical, endlessly SCROLLING strip
 * of short repeating text lines (`text` blocks) that slides upward forever via a pure-CSS `@keyframes` and a
 * top/bottom mask fade. On desktop the two sit side-by-side in a 2-col grid (collapsing to one column below
 * 900px); `layout` swaps which column comes first.
 *
 * Every Shrine section setting AND the per-line `text` block are implemented (same id where sensible,
 * Shrine default + options + group + visible_if → our visibleWhen), with the ticker's animation-duration,
 * per-breakpoint item font-size and inner max-height formulas ported EXACTLY from `vertical-ticker.liquid`
 * (Component.tsx). The content + ticker each carry their own 6-value colour scheme (+ custom colours),
 * mapped to the aquilixShop `--aqx-scheme-*` / `--aqx-theme-*` token contract (this codebase uses full
 * colours, not Shrine's rgb-triples — see theme.css).
 *
 * DEFERRED — Shopify-PLATFORM only, each rendered nothing off Shopify (reasons mirrored in Component.tsx):
 *  - display_id ......................... Shopify section-group merge copy-button; no off-Shopify meaning.
 *  - atc_button_label / atc_product /
 *    atc_skip_cart ...................... Shopify product object + cart form; needs a live product binding.
 */
const SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
export const verticalTickerSchema = {
    type: 'vertical-ticker',
    name: 'Vertical Ticker',
    version: 1,
    templates: ['index', 'product', 'collection', 'page'],
    fields: [
        // ── Display ──────────────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            group: 'Display',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'desktop-hidden', label: 'Mobile only' },
                { value: 'mobile-hidden', label: 'Desktop only' },
            ],
        },
        // ── Ticker ───────────────────────────────────────────────────────────────
        {
            id: 'number_of_rows',
            type: 'range',
            label: 'Number of rows displayed',
            group: 'Ticker',
            min: 4,
            max: 16,
            step: 1,
            default: 8,
        },
        {
            id: 'speed',
            type: 'range',
            label: 'Ticker speed',
            group: 'Ticker',
            min: 1,
            max: 5,
            step: 0.5,
            default: 3,
        },
        {
            id: 'text_size',
            type: 'select',
            label: 'Text size',
            group: 'Ticker',
            default: '4',
            options: [
                { value: '2', label: 'Extra small' },
                { value: '3', label: 'Small' },
                { value: '4', label: 'Medium' },
                { value: '5', label: 'Large' },
                { value: '6', label: 'Extra large' },
            ],
        },
        { id: 'italic_text', type: 'checkbox', label: 'Italic text', group: 'Ticker', default: false },
        {
            id: 'uppercase_text',
            type: 'checkbox',
            label: 'Uppercase text',
            group: 'Ticker',
            default: false,
        },
        { id: 'bold_text', type: 'checkbox', label: 'Bold text', group: 'Ticker', default: false },
        {
            id: 'ticker_color_scheme',
            type: 'select',
            label: 'Ticker color scheme',
            group: 'Ticker',
            default: 'accent-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'custom_ticker_colors_background',
            type: 'color',
            label: 'Custom background color',
            group: 'Ticker',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'ticker_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_ticker_gradient_background',
            type: 'gradient',
            label: 'Custom gradient background',
            group: 'Ticker',
            info: 'If added, replaces Background color when applicable.',
            visibleWhen: { field: 'ticker_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_ticker_colors_text',
            type: 'color',
            label: 'Custom text color',
            group: 'Ticker',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'ticker_color_scheme', equals: 'custom' },
        },
        {
            id: 'layout',
            type: 'select',
            label: 'Ticker placement',
            group: 'Ticker',
            default: 'ticker_second',
            options: [
                { value: 'ticker_first', label: 'Ticker first' },
                { value: 'ticker_second', label: 'Ticker second' },
            ],
        },
        {
            id: 'padding_top',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'padding_bottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 40,
        },
        // ── Content ──────────────────────────────────────────────────────────────
        {
            id: 'display_content',
            type: 'select',
            label: 'Display content',
            group: 'Content',
            default: 'always',
            options: [
                { value: 'always', label: 'Always' },
                { value: 'desktop_only', label: 'Desktop only' },
                { value: 'never', label: 'Never' },
            ],
        },
        {
            id: 'title',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Vertical ticker</p>' },
            info: 'Select words and apply Bold (B) for highlight 1, Italic (I) for highlight 2 — like the Text block.',
        },
        // Heading highlight engines: Bold (<strong>) → highlight 1, Italic (<em>) → highlight 2.
        ...headingHighlightFields(1, 'Heading highlight 1'),
        ...headingHighlightFields(2, 'Heading highlight 2'),
        {
            id: 'heading_size',
            type: 'select',
            label: 'Heading size',
            group: 'Content',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'custom_desktop_size',
            type: 'range',
            label: 'Custom desktop size',
            group: 'Content',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'heading_size', equals: 'custom' },
        },
        {
            id: 'custom_mobile_size',
            type: 'range',
            label: 'Custom mobile size',
            group: 'Content',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'heading_size', equals: 'custom' },
        },
        {
            id: 'content_text',
            type: 'richtext',
            label: 'Text',
            group: 'Content',
            default: {
                format: 'html',
                html: '<p>Talk about features, perks, or benefits of your brand or product</p>',
            },
        },
        {
            id: 'button_label',
            type: 'text',
            label: 'Button label',
            group: 'Content',
            default: 'Button label',
            defaultKey: 'section.button_label',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
            maxLength: 60,
            info: 'Leave blank to hide the button.',
        },
        { id: 'link', type: 'link', label: 'Button link', group: 'Content' },
        {
            id: 'button_style_secondary',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'Content',
            default: false,
        },
        {
            id: 'content_color_scheme',
            type: 'select',
            label: 'Content color scheme',
            group: 'Content',
            default: 'background-1',
            options: SCHEME_OPTIONS,
        },
        {
            id: 'custom_colors_background',
            type: 'color',
            label: 'Custom background color',
            group: 'Content',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_gradient_background',
            type: 'gradient',
            label: 'Custom gradient background',
            group: 'Content',
            info: 'If added, replaces Background color when applicable.',
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_text',
            type: 'color',
            label: 'Custom text color',
            group: 'Content',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_solid_button_background',
            type: 'color',
            label: 'Custom solid button background',
            group: 'Content',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_solid_button_text',
            type: 'color',
            label: 'Custom solid button label color',
            group: 'Content',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        {
            id: 'custom_colors_outline_button',
            type: 'color',
            label: 'Custom outline button color',
            group: 'Content',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'content_color_scheme', equals: 'custom' },
        },
        { id: 'content_bg_image', type: 'image', label: 'Background image', group: 'Content' },
        {
            id: 'overlay_color',
            type: 'color',
            label: 'Background overlay color',
            group: 'Content',
            default: { ref: 'custom', value: '#000000' },
        },
        {
            id: 'overlay_opacity',
            type: 'range',
            label: 'Background overlay opacity',
            group: 'Content',
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            default: 0,
        },
        {
            id: 'content_padding_top',
            type: 'range',
            label: 'Content top padding',
            group: 'Content',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'content_padding_bottom',
            type: 'range',
            label: 'Content bottom padding',
            group: 'Content',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 24,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // The ticker LINE block (Shrine `text`): a single short string, repeated down the scrolling strip.
    blocks: [
        {
            type: 'text',
            name: 'Text',
            max: 30,
            fields: [
                {
                    id: 'title',
                    type: 'text',
                    label: 'Text',
                    default: 'Shrine Theme',
                    defaultKey: 'section.brand_placeholder',
                    maxLength: 120,
                },
            ],
        },
    ],
    maxBlocks: 30,
};
const textBlock = () => ({ title: '@t:section.brand_placeholder' });
// Matches Shrine's `vertical-ticker` preset exactly: 4 text lines, accent-1 ticker, background-1 content,
// ticker second, default heading + text + button. (The render pasted from Shopify.)
export const verticalTickerPresets = [
    {
        name: 'Vertical Ticker',
        settings: {
            visibility: 'always-display',
            number_of_rows: 8,
            speed: 3,
            text_size: '4',
            italic_text: false,
            uppercase_text: false,
            bold_text: false,
            ticker_color_scheme: 'accent-1',
            layout: 'ticker_second',
            padding_top: 40,
            padding_bottom: 40,
            display_content: 'always',
            title: { format: 'html', html: '<p>Vertical ticker</p>' },
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            headingHighlight2: 'none',
            heading_size: 'h1',
            content_text: {
                format: 'html',
                html: '<p>Talk about features, perks, or benefits of your brand or product</p>',
            },
            button_style_secondary: false,
            content_color_scheme: 'background-1',
            overlay_color: { ref: 'custom', value: '#000000' },
            overlay_opacity: 0,
            content_padding_top: 36,
            content_padding_bottom: 24,
        },
        blocks: [
            { type: 'text', settings: textBlock() },
            { type: 'text', settings: textBlock() },
            { type: 'text', settings: textBlock() },
            { type: 'text', settings: textBlock() },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const verticalTickerMeta = {
    label: 'Vertical Ticker',
    group: 'Content',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map